import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Plus, Minus, CheckCircle, PaperPlaneTilt } from '@phosphor-icons/react';

function StudentQuizCreation() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [quizId, setQuizId] = useState(null);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [quizName, setQuizName] = useState('');
  const [isMultipleChoice, setIsMultipleChoice] = useState(false);
  const [answerOptions, setAnswerOptions] = useState(['']);
  const [selectedAnswers, setSelectedAnswers] = useState([0]);

  useEffect(() => {
    const fetchQuizId = async () => {
      const quizQuery = query(collection(db, 'quizzes'), where('code', '==', code));
      const quizSnapshot = await getDocs(quizQuery);
      if (!quizSnapshot.empty) {
        setQuizId(quizSnapshot.docs[0].id);
        setQuizName(quizSnapshot.docs[0].data().name);
      } else {
        alert('유효하지 않은 퀴즈 코드입니다.');
        navigate('/');
      }
    };
    fetchQuizId();
  }, [code, navigate]);

  const addAnswerOption = () => {
    setAnswerOptions([...answerOptions, '']);
  };

  const removeAnswerOption = (index) => {
    const newOptions = answerOptions.filter((_, i) => i !== index);
    setAnswerOptions(newOptions);
    setSelectedAnswers(selectedAnswers.filter(i => i !== index));
  };

  const handleAnswerOptionChange = (index, value) => {
    const newOptions = [...answerOptions];
    newOptions[index] = value;
    setAnswerOptions(newOptions);
  };

  const toggleAnswerSelection = (index) => {
    setSelectedAnswers(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const resetForm = () => {
    setQuestion('');
    setAnswer('');
    setAnswerOptions(['']);
    setSelectedAnswers([0]);
  };

  const submitQuiz = async (e) => {
    e.preventDefault();
    if (!quizId) return;

    if (isMultipleChoice) {
      if (answerOptions.some(option => !option.trim())) {
        alert('모든 보기를 입력해주세요.');
        return;
      }
      if (selectedAnswers.length === 0) {
        alert('정답을 하나 이상 선택해주세요.');
        return;
      }
    }

    await addDoc(collection(db, `quizzes/${quizId}/questions`), {
      question,
      answer: isMultipleChoice ? answerOptions[selectedAnswers[0]] : answer,
      isMultipleChoice,
      answerOptions: isMultipleChoice ? answerOptions : [answer],
      correctAnswers: isMultipleChoice ? selectedAnswers : [0],
      order: 0,
    });
    setIsSubmitted(true);
    resetForm();
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50/50 p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="pt-8 text-center space-y-6">
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle size={48} weight="fill" className="text-green-500" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">퀴즈가 제출되었습니다!</h2>
              <p className="text-gray-600">감사합니다. 선생님이 퀴즈를 시작하면 참여할 수 있습니다.</p>
            </div>
            <Button onClick={() => setIsSubmitted(false)} className="w-full">
              <Plus size={20} className="mr-2" weight="bold" />
              다른 질문 추가하기
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 py-20">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl">퀴즈 제작</CardTitle>
            <CardDescription className="text-lg">{quizName}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submitQuiz} className="space-y-6">
              {/* Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <Label className="text-sm font-medium">선다형 문제</Label>
                <div
                  onClick={() => setIsMultipleChoice(!isMultipleChoice)}
                  className={`w-12 h-6 rounded-full p-1 cursor-pointer transition-colors ${isMultipleChoice ? 'bg-primary' : 'bg-gray-300'}`}
                >
                  <div className={`w-4 h-4 rounded-full bg-white transition-transform ${isMultipleChoice ? 'translate-x-6' : 'translate-x-0'}`} />
                </div>
              </div>

              {/* Question */}
              <div className="space-y-2">
                <Label htmlFor="question">퀴즈 질문</Label>
                <Input
                  id="question"
                  type="text"
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="퀴즈 질문을 입력하세요"
                  className="bg-gray-50"
                  required
                />
              </div>

              {/* Answer Options */}
              {isMultipleChoice ? (
                <div className="space-y-3">
                  <Label>보기</Label>
                  {answerOptions.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div
                        onClick={() => toggleAnswerSelection(index)}
                        className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all flex-1 ${selectedAnswers.includes(index)
                          ? 'bg-primary/10 border-2 border-primary'
                          : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                          }`}
                      >
                        <div className={`w-6 h-6 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-all ${selectedAnswers.includes(index)
                          ? 'border-primary bg-primary'
                          : 'border-gray-300'
                          }`}>
                          {selectedAnswers.includes(index) && (
                            <CheckCircle size={16} weight="fill" className="text-white" />
                          )}
                        </div>
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => handleAnswerOptionChange(index, e.target.value)}
                          className={`flex-1 bg-transparent border-none focus:outline-none focus:ring-0 ${selectedAnswers.includes(index)
                            ? 'text-primary font-medium'
                            : 'text-gray-700'
                            }`}
                          placeholder={`보기 ${index + 1}`}
                          required
                        />
                      </div>
                      <div className="flex gap-1">
                        {index > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeAnswerOption(index)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Minus size={18} />
                          </Button>
                        )}
                        {index === answerOptions.length - 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={addAnswerOption}
                            className="text-primary hover:bg-primary/10"
                          >
                            <Plus size={18} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="answer">퀴즈 정답</Label>
                  <Input
                    id="answer"
                    type="text"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="정답을 입력하세요"
                    className="bg-gray-50"
                    required
                  />
                </div>
              )}

              <Button type="submit" className="w-full h-12 text-lg">
                <PaperPlaneTilt size={20} className="mr-2" weight="fill" />
                질문 추가
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default StudentQuizCreation;